/**
 * Map Configuration Module
 * Handles map initialization, base layers, and controls
 */

import { selectedLanguage, layersTranslation } from '../utils/translation';

// Map and layer group instances (initialized in initializeMap)
let _map = null;
let _markers = null;
let _controlAreaLayers = null;
let _allEventsLayers = null;
let _sidebar = null;

// Getter functions to ensure other modules get the current instance
export const getMap = () => {
    console.log('getMap() called, returning:', _map ? 'map instance' : 'null');
    return _map;
};
export const getMarkers = () => {
    console.log('getMarkers() called, returning:', _markers ? 'markers instance' : 'null');
    return _markers;
};
export const getControlAreaLayers = () => {
    console.log('getControlAreaLayers() called, returning:', _controlAreaLayers ? 'controlAreaLayers instance' : 'null');
    return _controlAreaLayers;
};
export const getAllEventsLayers = () => {
    console.log('getAllEventsLayers() called, returning:', _allEventsLayers ? 'allEventsLayers instance' : 'null');
    return _allEventsLayers;
};
export const getSidebar = () => {
    console.log('getSidebar() called, returning:', _sidebar ? 'sidebar instance' : 'null');
    return _sidebar;
};

// Export for backwards compatibility (will be updated after init)
export let map = null;
export let markers = null;
export let controlAreaLayers = null;
export let allEventsLayers = null;
export let sidebar = null;

/**
 * Initialize base map layers
 */
export function initializeBaseLayers() {
    const baseLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: 'AY Maps &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors',
    }).addTo(_map);

    const blueprintLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: 'AY Maps &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors',
    });

    const humanitarianLayer = L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
        attribution: 'AY Maps &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, Humanitarian OpenStreetMap Team',
    });

    const openTopoMapLayer = L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
        attribution: 'AY Maps &copy; <a href="https://www.opentopomap.org/">OpenTopoMap</a> contributors',
    });

    // ArcGIS API Key from environment variable
    // const arcgis_api_key = "AAPK858d2e9d69ae4cb2bd9b9ce11c55e206wHrE4-UEf6JIaZkvvQsKOa4U4iUaIs1LP9RaOanF5chcXggUJOTLFLQ_yUZsM6pl";

    // const esriLightMapLayer  = L.tileLayer(`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}?token=${arcgis_api_key}`, {
    //     attribution: 'AY Maps &copy;',
    // });


    

    const basemapLayers = {
        "Standard": baseLayer,
        
        // "Blueprint": blueprintLayer,
        // "Humanitarian": humanitarianLayer,
        // "OpenTopoMap": openTopoMapLayer,
    };

    return basemapLayers;
}

// Process Esri Layers
export function initializeEsriLayer() {

        const arcgis_api_key = process.env.VITE_ARCGIS_API_KEY;
        
        const basemapLayers = {
        "light": getBasemap("arcgis/light-gray"),
        "streets": getBasemap("arcgis/streets"),
        "imagery": getBasemap("arcgis/imagery"),
        "dark-gray": getBasemap("arcgis/dark-gray"),
        "human-geography": getBasemap("arcgis/human-geography"),
        "charted-territory": getBasemap("arcgis/charted-territory"),
        "nova": getBasemap("arcgis/nova"),
        "standard": getBasemap("osm/standard").addTo(map),
        "blueprint": getBasemap("osm/blueprint")
        };

        function getBasemap(basemap) {
            return L.esri.Vector.vectorBasemapLayer(basemap, {
                apiKey: arcgis_api_key,
                language: selectedLanguage || 'global',
                version: 2,
                attribution: '© 2024 AY Maps'
            });
        }
        L.esri.Vector.setRTLTextPlugin('https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.js');
        return basemapLayers;
}

/**
 * Initialize layer controls
 */
export function initializeLayerControls() {
    // const basemapLayers = initializeBaseLayers();
    const basemapLayers = initializeEsriLayer();
    const layerControl = layersTranslation();
    
    L.control.layers(basemapLayers, {
        [layerControl["Control Area"]]: _controlAreaLayers,
        [layerControl["events"]]: _allEventsLayers,
    }, { 
        position: selectedLanguage === 'ar' ? 'topright' : 'topleft' 
    }).addTo(_map);
}

/**
 * Initialize sidebar
 */
export function initializeSidebar() {
    _sidebar = sidebar = L.control.sidebar({ 
        container: "sidebar", 
        position: selectedLanguage === 'ar' ? 'right' : 'left',
        closeButton: true,
        autopan: false
    }).addTo(_map);
    
    // Function to close sidebar
    const closeSidebar = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        console.log('Closing sidebar via close button');
        
        const sidebarElement = document.getElementById('sidebar');
        if (sidebarElement) {
            sidebarElement.classList.add('collapsed');
            console.log('Added collapsed class to sidebar');
        }
        
        // Also close via Leaflet API
        if (_sidebar && typeof _sidebar.close === 'function') {
            _sidebar.close();
        }
    };
    
    // Add event listeners to custom close buttons
    setTimeout(() => {
        const closeButtons = document.querySelectorAll('.sidebar-close-btn');
        console.log('Found', closeButtons.length, 'close buttons');
        
        closeButtons.forEach((btn, index) => {
            console.log(`Setting up close button ${index + 1}`);
            
            // Remove any existing listeners by cloning
            const newBtn = btn.cloneNode(true);
            btn.parentNode.replaceChild(newBtn, btn);
            
            // Click event - use mousedown for better mobile response
            newBtn.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                closeSidebar(e);
            });
            
            // Touch events for mobile - separate handlers
            let touchStarted = false;
            
            newBtn.addEventListener('touchstart', (e) => {
                touchStarted = true;
                e.stopPropagation();
                console.log('Touch started on close button');
            }, { passive: false });
            
            newBtn.addEventListener('touchend', (e) => {
                if (touchStarted) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Touch ended on close button - closing');
                    closeSidebar(e);
                    touchStarted = false;
                }
            }, { passive: false });
            
            newBtn.addEventListener('touchcancel', () => {
                touchStarted = false;
            }, { passive: true });
        });
        
        // On mobile, clicking map should close sidebar
        if (window.innerWidth <= 768) {
            _map.on('click', () => {
                const sidebarElement = document.getElementById('sidebar');
                if (sidebarElement && !sidebarElement.classList.contains('collapsed')) {
                    console.log('Map clicked, closing sidebar');
                    closeSidebar();
                }
            });
        }
        
        // ESC key to close sidebar
        document.addEventListener('keydown', (e) => {
            const sidebarElement = document.getElementById('sidebar');
            if (e.key === 'Escape' && sidebarElement && !sidebarElement.classList.contains('collapsed')) {
                console.log('ESC pressed, closing sidebar');
                closeSidebar();
            }
        });
    }, 300);
    
    return _sidebar;
}

/**
 * Measurement units configuration
 */
const languageOfMeasurementUnits = {
    'ar': {
        "linearMeasurement": "قياس المسافة",
        "areaMeasurement": "قياس المساحة",
        "start": "نقطة البداية",
        "meter": "م",
        "kilometer": "كم",
        "squareMeter": "م²",
        "squareKilometers": "كم²"
    },
    'en': {
        "linearMeasurement": "Distance measurement",
        "areaMeasurement": "Area measurement",
        "start": "start",
        "meter": "m",
        "kilometer": "km",
        "squareMeter": "m²",
        "squareKilometers": "km²"
    },
    'tr': {
        "linearMeasurement": "Mesafe hesaplama",
        "areaMeasurement": "Alan Ölçümü",
        "start": "başlangıç nokta",
        "meter": "m",
        "kilometer": "km",
        "squareMeter": "m²",
        "squareKilometers": "km²"
    }
};

/**
 * Initialize measurement control
 */
export function initializeMeasureControl() {
    // Check if measure control is available
    if (typeof L.control.measure === 'undefined') {
        console.warn('Leaflet measure control plugin not loaded - skipping measure control initialization');
        return;
    }
    
    L.Measure = languageOfMeasurementUnits[selectedLanguage] || languageOfMeasurementUnits['en'];
    
    L.control.measure({
        title: "",
        position: selectedLanguage === "ar" ? 'topleft' : 'topright',
        primaryLengthUnit: 'meters',
        secondaryLengthUnit: 'kilometers',
        primaryAreaUnit: 'sqmeters',
        secondaryAreaUnit: 'hectares',
        activeColor: '#ABE67E',
        completedColor: '#C8F2BE'
    }).addTo(_map);
}

/**
 * Initialize floating menu button for mobile
 */
function initializeFloatingMenuButton() {
    const floatingMenuBtn = document.getElementById('floatingMenuBtn');
    
    if (floatingMenuBtn) {
        floatingMenuBtn.addEventListener('click', () => {
            const sidebarElement = document.getElementById('sidebar');
            if (sidebarElement) {
                const isCollapsed = sidebarElement.classList.contains('collapsed');
                
                if (isCollapsed) {
                    // Open sidebar and show language pane
                    sidebarElement.classList.remove('collapsed');
                    
                    // Manually activate the language pane
                    const languagePane = document.getElementById('language');
                    const allPanes = document.querySelectorAll('.leaflet-sidebar-pane');
                    
                    // Remove active from all panes
                    allPanes.forEach(pane => pane.classList.remove('active'));
                    
                    // Add active to language pane
                    if (languagePane) {
                        languagePane.classList.add('active');
                    }
                    
                    // Also use leaflet API if available
                    if (_sidebar && typeof _sidebar.open === 'function') {
                        _sidebar.open('language');
                    }
                    
                    console.log('Sidebar opened via menu button');
                } else {
                    // Close sidebar
                    sidebarElement.classList.add('collapsed');
                    
                    // Remove active from all panes
                    const allPanes = document.querySelectorAll('.leaflet-sidebar-pane');
                    allPanes.forEach(pane => pane.classList.remove('active'));
                    
                    if (_sidebar && typeof _sidebar.close === 'function') {
                        _sidebar.close();
                    }
                    console.log('Sidebar closed via menu button');
                }
            }
        });
    }
}

/**
 * Initialize all map configurations
 */
export function initializeMap() {
    console.log('Initializing map...');
    
    // Check if map div exists
    const mapDiv = document.getElementById('map');
    if (!mapDiv) {
        console.error('Map div not found!');
        return;
    }
    
    // Create the map instance with a default view
    _map = map = L.map('map', { 
        maxZoom: 18, 
        minZoom: 6, 
        zoomControl: false 
    }).setView([34.8021, 38.9968], 7); // Default to Syria center
    
    console.log('Map created:', _map);
    
    // Create layer groups
    _markers = markers = L.markerClusterGroup();
    _controlAreaLayers = controlAreaLayers = L.layerGroup().addTo(_map);
    _allEventsLayers = allEventsLayers = L.layerGroup().addTo(_map);
    
    console.log('Layer groups created');
    
    // Add zoom control
    L.control.zoom({
        position: selectedLanguage === 'ar' ? 'topleft' : 'topright'
    }).addTo(_map);
    
    console.log('Zoom control added');
    
    initializeLayerControls();
    console.log('Layer controls initialized');
    
    initializeSidebar();
    console.log('Sidebar initialized');
    
    initializeFloatingMenuButton();
    console.log('Floating menu button initialized');
    
    // TODO: Re-enable measure control in future updates
    // initializeMeasureControl();
    // console.log('Measure control initialized');
    
    console.log('Map initialization complete!');
    
    // Force map to invalidate size (ensures proper rendering)
    setTimeout(() => {
        _map.invalidateSize();
        console.log('Map size invalidated');
    }, 100);
}
